import React, { useState, useContext, useEffect } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

import axios from '../../../sharedModules/utils/axios.utils';

import PreviousLogUnitMiniTable from './PreviousLogUnitMiniTable';
import PreviousLogCircuitMiniTable from './PreviousLogCircuitMiniTable';
import PreviousLogCompressorMiniTable from './PreviousLogCompressorMiniTable';

import { ChillerLogsContext } from '../contexts/ChillerLogsContext';

const PreviousLogDialog: React.FunctionComponent = (props) => {
  let { previousLogDialogOpen, setPreviousLogDialogOpen, logForPreviousLogModal } = useContext(ChillerLogsContext);

  let [previousLogData, setPreviousLogData] = useState<any>(null);
  let [currentChillerLog, setCurrentChillerLog] = useState<any>(null);

  const fetchPreviousLogData = async (logId: string) => {
    try {
      const response = await axios.get<any, any>(`/chiller_logs/chiller_logs/${logId}`);
      setPreviousLogData(response?.data?.result);
      setCurrentChillerLog(response?.data?.result?.spreadsheet_details);
    } catch (error) {
      console.error('Error fetching previous log data:', error);
    }
  };

  useEffect(() => {
    if (logForPreviousLogModal?.id) {
      fetchPreviousLogData(logForPreviousLogModal?.id);
    }
  }, [logForPreviousLogModal]);

  const getValidCircuits = () => {
    if (!currentChillerLog?.circuits) return [];
    const circuitCount = currentChillerLog?.circuit_count || currentChillerLog?.circuits?.length || 0;
    return currentChillerLog.circuits
      .filter((circuit) => circuit.index < circuitCount)
      .sort((a, b) => a.index - b.index);
  };

  const buildCircuitRowValues = (row: any, rowIndex: number, validCircuits: any[], whichDetail: string) => {
    // Get the display name (field label) from the first circuit's template
    const circuitValues: any = {
      display_name: row?.[0]?.display_name,
    };

    // Pull the actual values from each circuit for this row
    validCircuits.forEach((circuit, circuitIndex) => {
      const value = circuit?.[whichDetail]?.[rowIndex]?.[0]?.value;
      circuitValues[`circuit${circuitIndex + 1}Value`] = value;
    });

    return circuitValues;
  };

  const circuitDetails = (whichDetail) => {
    // The first circuit contains the template/structure (display names) for detail rows.
    // All circuits share the same field structure, so we use the first circuit to get the row definitions.
    const firstCircuit = currentChillerLog?.circuits?.[0];
    if (!firstCircuit?.id) return;

    const validCircuits = getValidCircuits();
    const detailRows = firstCircuit[whichDetail];
    if (!detailRows) return;

    return detailRows
      .map((row, rowIndex) => buildCircuitRowValues(row, rowIndex, validCircuits, whichDetail))
      .filter((detail) => detail.display_name !== 'Submit Date');
  };

  return (
    <Dialog open={previousLogDialogOpen} onClose={() => setPreviousLogDialogOpen(false)} fullWidth maxWidth="md">
      <DialogTitle>Previous Submission Data ({previousLogData?.submit_dt})</DialogTitle>
      <DialogContent>
        <PreviousLogUnitMiniTable data={currentChillerLog?.header_details} title="Header" />
        <PreviousLogCircuitMiniTable data={circuitDetails('evaporator_details')} title="Evaporator" />
        <PreviousLogCircuitMiniTable data={circuitDetails('condenser_details')} title="Condenser" />
        {getValidCircuits().map((circuit, index) => {
          return <PreviousLogCompressorMiniTable key={index} circuit={circuit} />;
        })}
        {currentChillerLog?.circuits?.[0]?.york_circuit_details && (
          <PreviousLogCircuitMiniTable
            data={currentChillerLog?.circuits?.[0]?.york_circuit_details}
            title="York Circuit Details"
          />
        )}
        <PreviousLogUnitMiniTable data={currentChillerLog?.additional_details} title="Additional" />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPreviousLogDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviousLogDialog;
